import { useQuery } from "@apollo/client";
import { Combobox } from "@/components/Combobox";
import { type ComboboxOption } from "@/components/Combobox/types";
import { graphql, type ResultOf } from "@/graphql";
import { useActiveBusinessUnit } from "@/hooks/use-active-business-unit";
import { removeNothings } from "@/lib/utils/arrays";

type FeedyardId = string;

type Props = {
  id?: string;
  selected: Array<FeedyardId>;
  onSelect: (values: Array<FeedyardId>) => void;
  className?: string;
};

export type FeedyardsData = ResultOf<typeof supplyChainLotFeedyardsQuery>;

export function FeedyardPicker({ id, selected, onSelect, className }: Props) {
  const { activeBusinessUnitId } = useActiveBusinessUnit();
  const { data, loading } = useQuery(supplyChainLotFeedyardsQuery, {
    variables: { businessUnit: activeBusinessUnitId || "" },
    skip: !activeBusinessUnitId,
  });

  const options: Array<ComboboxOption> = getFeedyardsOptions(data);

  return (
    <Combobox
      id={id}
      type="multi"
      name="feedyards"
      options={options}
      selected={selected}
      onChange={onSelect}
      loading={loading}
      className={className}
    />
  );
}

export const getFeedyardsOptions = (data: FeedyardsData | undefined): Array<ComboboxOption> => {
  return removeNothings(data?.supplyChainPartners?.edges || [])
    .map((edge) => ({
      label: edge.node?.businessName || "",
      value: edge.node?.id || "",
    }))
    .filter((option) => option.value !== "");
};

// Feedyards are supply chain partners with a feeder role. The picker is keyed by the
// partner UUID, which is what the supplyChainLots `feedyardPartners` filter expects.
export const supplyChainLotFeedyardsQuery = graphql(`
  query SupplyChainLotFeedyards($businessUnit: ID!) {
    supplyChainPartners(
      owners: [$businessUnit]
      roles: [CUSTOM_FEEDER, OWNER_FEEDER]
      status: [ACTIVE]
      sortBy: [{ field: BUSINESS_NAME, desc: false }]
    ) {
      edges {
        node {
          id
          businessName
        }
      }
    }
  }
`);
